
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AlertCircle, Check, Key, Loader2, PlusCircle, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { ApiKey } from "@/models/api-key";
import { ApiKeyService } from "@/services/api-key-service";

interface ApiKeyCardProps {
  id: string;
  provider: string;
  maskedKey: string;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

const ApiKeyCard: React.FC<ApiKeyCardProps> = ({ id, provider, maskedKey, onDelete, isDeleting }) => {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Key className="h-4 w-4" />
          {provider}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="font-mono text-sm text-muted-foreground">{maskedKey}</div>
      </CardContent>
      <CardFooter className="p-2 flex justify-end bg-muted/50">
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => onDelete(id)}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          <span className="sr-only">Delete</span>
        </Button>
      </CardFooter>
    </Card>
  );
};

export function ApiKeyManager() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [provider, setProvider] = useState("OpenAI");
  const [apiKey, setApiKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  
  useEffect(() => {
    loadApiKeys();
  }, []);
  
  const loadApiKeys = async () => {
    setLoading(true);
    try {
      const keys = await ApiKeyService.getApiKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error("Failed to load API keys:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddKey = async () => {
    if (!apiKey.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter an API key",
      });
      return;
    }
    
    setIsSubmitting(true);
    try {
      const newKey = await ApiKeyService.createApiKey({
        provider,
        key: apiKey
      });
      
      if (newKey) {
        // Refresh the list
        await loadApiKeys();
        setApiKey("");
        setOpen(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDeleteKey = async (id: string) => {
    setDeletingKey(id);
    try {
      const success = await ApiKeyService.deleteApiKey(id);
      if (success) {
        // Remove the key from the list
        setApiKeys(apiKeys.filter(key => key.id !== id));
      }
    } finally {
      setDeletingKey(null);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Key className="h-5 w-5" />
            LLM API Keys
          </h2>
          <p className="text-sm text-muted-foreground">Connect to AI providers by adding your API keys</p>
        </div>
        
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add API Key
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add a new API key</DialogTitle>
              <DialogDescription>
                Connect your AI provider by adding your API key. Keys are stored securely.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>AI Provider</Label>
                <RadioGroup value={provider} onValueChange={setProvider} className="flex flex-col space-y-1">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="OpenAI" id="openai" />
                    <Label htmlFor="openai">OpenAI</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Anthropic" id="anthropic" />
                    <Label htmlFor="anthropic">Anthropic</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Google" id="google" />
                    <Label htmlFor="google">Google AI</Label>
                  </div>
                </RadioGroup>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="apiKey">API Key</Label>
                <Input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your API key"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="button" 
                onClick={handleAddKey} 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>Add API Key</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      {loading ? (
        <div className="flex justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : apiKeys.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No API Keys Added</CardTitle>
            <CardDescription>
              Add your first API key to connect with AI providers.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="outline" onClick={() => setOpen(true)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add API Key
            </Button>
          </CardFooter>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {apiKeys.map((key) => (
            <ApiKeyCard
              key={key.id}
              id={key.id}
              provider={key.provider}
              maskedKey={key.maskedKey}
              onDelete={handleDeleteKey}
              isDeleting={deletingKey === key.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
